import React, { useState, useEffect, useRef } from 'react';
import { Page } from '../types';
import { ChevronDownIcon, MenuIcon, XIcon, SunIcon, MoonIcon, SearchIcon } from './IconComponents';
import { useTheme } from '../contexts/ThemeContext';
import { useRouter } from '../contexts/RouterContext';
import Tooltip from './Tooltip';
import { cmaParts } from '../data/papers';
import type { CmaPart } from '../data/papers';
import Link from './Link';
import TopBanner from './TopBanner';

// Reusable component for mobile accordions
const MobileNavLinkAccordion: React.FC<{ title: string; children: React.ReactNode; defaultOpen?: boolean; level?: number; closeMobileMenu: () => void }> = ({ title, children, defaultOpen = false, level = 0, closeMobileMenu }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    const paddingLeftClass = 'pl-6';
    const bgClass = level === 0 ? 'bg-slate-50 dark:bg-zinc-900' : 'bg-slate-100 dark:bg-zinc-800';

    // Wrap children to attach onClick for closing the menu
    const childrenWithClose = React.Children.map(children, child => {
        if (React.isValidElement<{ onClick?: (e: React.MouseEvent) => void }>(child) && 'to' in child.props) {
            return React.cloneElement(child, {
                onClick: (e: React.MouseEvent) => {
                    if (child.props.onClick) {
                        child.props.onClick(e);
                    }
                    closeMobileMenu();
                }
            });
        }
        return child;
    });

    return (
        <div className="w-full border-b border-slate-200 dark:border-zinc-800">
            <button
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
                className={`w-full flex justify-between items-center py-3 ${paddingLeftClass} pr-6 text-left text-slate-700 dark:text-slate-200`}
            >
                <span className="font-semibold">{title}</span>
                <ChevronDownIcon className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            <div
                className={`grid transition-[grid-template-rows] duration-500 ease-in-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
            >
                <div className="overflow-hidden">
                    <div className={`py-2 flex flex-col ${bgClass}`}>
                        {childrenWithClose}
                    </div>
                </div>
            </div>
        </div>
    );
};

interface HeaderProps {
    onOpenSearch: () => void;
}

const Header: React.FC<HeaderProps> = ({ onOpenSearch }) => {
  const [isDetailsDropdownOpen, setDetailsDropdownOpen] = useState(false);
  const [isEnrollDropdownOpen, setEnrollDropdownOpen] = useState(false);
  const [isResourcesDropdownOpen, setResourcesDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMac, setIsMac] = useState(false);
  const detailsDropdownRef = useRef<HTMLDivElement>(null);
  const enrollDropdownRef = useRef<HTMLDivElement>(null);
  const resourcesDropdownRef = useRef<HTMLDivElement>(null);
  const { theme, toggleTheme } = useTheme();
  const { navigateTo, currentNav } = useRouter();

  useEffect(() => {
    setIsMac(navigator.platform.toUpperCase().indexOf('MAC') >= 0);
    
    const handleClickOutside = (event: MouseEvent) => {
      if (detailsDropdownRef.current && !detailsDropdownRef.current.contains(event.target as Node)) setDetailsDropdownOpen(false);
      if (enrollDropdownRef.current && !enrollDropdownRef.current.contains(event.target as Node)) setEnrollDropdownOpen(false);
      if (resourcesDropdownRef.current && !resourcesDropdownRef.current.contains(event.target as Node)) setResourcesDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeDropdowns = () => {
    setDetailsDropdownOpen(false);
    setEnrollDropdownOpen(false);
    setResourcesDropdownOpen(false);
  };

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigateTo({ page: 'contact' });
    setMobileMenuOpen(false);
  };

  const cmaDetailsPages: Page[] = ['cma-course-details', 'cma-experience-requirement', 'cma-course-fee', 'cma-faqs'];
  const enrollPages: Page[] = ['enroll-now', 'part-detail', 'cma-bundle-offer'];
  const resourcePages: Page[] = ['demo-videos', 'articles', 'official-resources', 'article-detail'];

  const isCmaDetailsActive = cmaDetailsPages.includes(currentNav.page);
  const isEnrollActive = enrollPages.includes(currentNav.page);
  const isResourcesActive = resourcePages.includes(currentNav.page);


  const activeLinkClasses = 'border-brand-accent dark:border-brand-accent-dark text-brand-accent dark:text-brand-accent-dark';
  const inactiveLinkClasses = 'border-transparent hover:text-brand-accent dark:hover:text-brand-accent-dark';
  const baseLinkClasses = 'py-2 border-b-2 transition-colors duration-300';

  return (
    <>
      <TopBanner />
      <header className="bg-white/80 dark:bg-black/80 backdrop-blur-sm text-brand-blue dark:text-white shadow-lg sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
        <Link to={{ page: 'home' }} className="flex items-center cursor-pointer">
          <img 
            src="https://res.cloudinary.com/dxohyfpnf/image/upload/h_80,c_scale,f_auto,q_auto/v1760489347/Black_w0ihji_ett9dp.webp" 
            alt="Global Fin X Logo" 
            width="104"
            height="40"
            className="h-10 w-auto dark:hidden" 
            style={{ aspectRatio: '104 / 40' }}
          />
          <img 
            src="https://res.cloudinary.com/dxohyfpnf/image/upload/h_80,c_scale,f_auto,q_auto/v1760489348/White_bdvvxy_ovzvgt.webp" 
            alt="Global Fin X Logo" 
            width="104"
            height="40"
            className="h-10 w-auto hidden dark:block" 
            style={{ aspectRatio: '104 / 40' }}
          />
        </Link>
        
        <div className="hidden md:flex items-center space-x-4">
            <nav className="flex items-center space-x-6">
              <Link
                to={{ page: 'home' }}
                className={`${baseLinkClasses} cursor-pointer ${inactiveLinkClasses}`}
                activeClassName={activeLinkClasses}
              >
                Home
              </Link>
              
              <div 
                className="relative" 
                ref={detailsDropdownRef}
                onMouseEnter={() => setDetailsDropdownOpen(true)}
                onMouseLeave={() => setDetailsDropdownOpen(false)}
              >
                <button
                  onClick={() => setDetailsDropdownOpen(!isDetailsDropdownOpen)}
                  aria-expanded={isDetailsDropdownOpen}
                  aria-haspopup="true"
                  className={`${baseLinkClasses} flex items-center space-x-1 ${isCmaDetailsActive ? activeLinkClasses : inactiveLinkClasses}`}
                >
                  <span>CMA US Details</span>
                  <ChevronDownIcon className={`w-4 h-4 transition-transform duration-300 ${isDetailsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {isDetailsDropdownOpen && (
                  <div className="absolute top-full z-20 w-56 bg-white dark:bg-zinc-900 rounded-lg shadow-xl py-2">
                    <Link to={{ page: 'cma-course-details' }} onClick={closeDropdowns} className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-zinc-800 cursor-pointer">Course Details</Link>
                    <Link to={{ page: 'cma-experience-requirement' }} onClick={closeDropdowns} className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-zinc-800 cursor-pointer">Experience Requirement</Link>
                    <Link to={{ page: 'cma-course-fee' }} onClick={closeDropdowns} className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-zinc-800 cursor-pointer">Course Fee</Link>
                    <Link to={{ page: 'cma-faqs' }} onClick={closeDropdowns} className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-zinc-800 cursor-pointer">FAQs</Link>
                  </div>
                )}
              </div>
              
              <div 
                className="relative flex justify-center" 
                ref={enrollDropdownRef}
                onMouseEnter={() => setEnrollDropdownOpen(true)}
                onMouseLeave={() => setEnrollDropdownOpen(false)}
              >
                <button
                    onClick={() => setEnrollDropdownOpen(!isEnrollDropdownOpen)}
                    aria-expanded={isEnrollDropdownOpen}
                    aria-haspopup="true"
                    className={`${baseLinkClasses} flex items-center space-x-1 ${isEnrollActive ? activeLinkClasses : inactiveLinkClasses}`}
                >
                    <span>Enroll with GFX</span>
                    <ChevronDownIcon className={`w-4 h-4 transition-transform duration-300 ${isEnrollDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {isEnrollDropdownOpen && (
                     <div className="absolute top-full z-20 bg-white dark:bg-zinc-900 rounded-lg shadow-xl py-2 w-max">
                        {cmaParts.map(part => (
                            <Link
                                key={part.code}
                                to={{ page: 'part-detail', params: { code: part.code } }}
                                onClick={closeDropdowns}
                                className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-zinc-800 cursor-pointer"
                            >
                                {part.title}
                            </Link>
                        ))}
                        <div className="border-t border-slate-200 dark:border-zinc-800 my-1"></div>
                        <Link 
                            to={{ page: 'cma-bundle-offer' }} 
                            onClick={closeDropdowns} 
                            className="block px-4 py-2 text-sm text-brand-accent dark:text-brand-accent-dark font-semibold hover:bg-slate-100 dark:hover:bg-zinc-800 cursor-pointer"
                        >
                            Special Offer: Both Parts
                        </Link>
                      </div>
                  )}
              </div>

              <Link
                to={{ page: 'testimonials' }}
                className={`${baseLinkClasses} cursor-pointer ${inactiveLinkClasses}`}
                activeClassName={activeLinkClasses}
              >
                Testimonials
              </Link>
              
              <div
                className="relative"
                ref={resourcesDropdownRef}
                onMouseEnter={() => setResourcesDropdownOpen(true)}
                onMouseLeave={() => setResourcesDropdownOpen(false)}
              >
                <button
                    onClick={() => setResourcesDropdownOpen(!isResourcesDropdownOpen)}
                    aria-expanded={isResourcesDropdownOpen}
                    aria-haspopup="true"
                    className={`${baseLinkClasses} flex items-center space-x-1 ${isResourcesActive ? activeLinkClasses : inactiveLinkClasses}`}
                >
                    <span>Resources</span>
                    <ChevronDownIcon className={`w-4 h-4 transition-transform duration-300 ${isResourcesDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {isResourcesDropdownOpen && (
                    <div className="absolute top-full z-20 w-56 bg-white dark:bg-zinc-900 rounded-lg shadow-xl py-2">
                        <Link to={{ page: 'demo-videos' }} onClick={closeDropdowns} className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-zinc-800 cursor-pointer">Demo Videos</Link>
                        <Link to={{ page: 'articles' }} onClick={closeDropdowns} className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-zinc-800 cursor-pointer">Articles</Link>
                        <Link to={{ page: 'official-resources' }} onClick={closeDropdowns} className="block px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-zinc-800 cursor-pointer">Official Resources</Link>
                    </div>
                )}
              </div>

              <Link
                to={{ page: 'about' }}
                className={`${baseLinkClasses} cursor-pointer ${inactiveLinkClasses}`}
                activeClassName={activeLinkClasses}
              >
                About us
              </Link>
            </nav>
            <div className="flex items-center space-x-2 border-l border-slate-200 dark:border-zinc-800 pl-4">
                <button
                    onClick={onOpenSearch}
                    aria-label="Search"
                    className="flex items-center justify-between w-40 text-sm text-slate-500 dark:text-slate-400 bg-white dark:bg-zinc-800 ring-1 ring-slate-200 dark:ring-zinc-700 hover:ring-brand-accent dark:hover:ring-brand-accent-dark rounded-md py-1.5 px-2 transition-all"
                >
                    <span className="flex items-center">
                        <SearchIcon className="w-4 h-4 mr-2" />
                        Search...
                    </span>
                    <kbd className="inline-flex items-center px-1.5 py-0.5 text-xs font-sans font-semibold text-slate-700 bg-slate-100 dark:bg-zinc-700 rounded border border-slate-200 dark:border-zinc-600">
                        {isMac ? '⌘K' : 'Ctrl+K'}
                    </kbd>
                </button>
                <Tooltip text={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}>
                    <button
                    onClick={toggleTheme}
                    aria-label="Toggle theme"
                    className="p-2 rounded-full text-brand-accent dark:text-brand-accent-dark hover:bg-slate-800/10 dark:hover:bg-white/10 transition-colors"
                    >
                    {theme === 'light' ? <MoonIcon className="w-5 h-5" /> : <SunIcon className="w-5 h-5" />}
                    </button>
                </Tooltip>
                 <button
                    onClick={() => navigateTo({ page: 'cma-bundle-offer' })}
                    className="bg-brand-accent dark:bg-brand-accent-dark text-white font-bold py-2 px-4 rounded-full text-sm hover:bg-opacity-90 transition-all duration-300 transform hover:scale-105"
                >
                    Enroll Now
                </button>
                <button
                    onClick={handleContactClick}
                    className="bg-transparent text-brand-blue dark:text-white border-2 border-brand-blue dark:border-white font-bold py-2 px-4 rounded-full text-sm hover:bg-brand-blue hover:text-white dark:hover:bg-white dark:hover:text-brand-blue transition-all duration-300 transform hover:scale-105"
                >
                    Contact Us
                </button>
            </div>
        </div>

        <div className="md:hidden flex items-center space-x-2">
            <Tooltip text="Search">
                <button onClick={onOpenSearch} aria-label="Open search" className="p-2 rounded-full text-brand-blue dark:text-white">
                    <SearchIcon className="w-6 h-6" />
                </button>
            </Tooltip>
          <button 
            onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <XIcon className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white dark:bg-black">
          <nav className="flex flex-col">
            <Link to={{ page: 'home' }} onClick={() => setMobileMenuOpen(false)} className="w-full text-left py-3 px-6 border-b border-slate-200 dark:border-zinc-800 font-semibold cursor-pointer">Home</Link>
            
            <MobileNavLinkAccordion title="CMA US Details" closeMobileMenu={() => setMobileMenuOpen(false)}>
                <Link to={{ page: 'cma-course-details' }} className="py-2 px-6 text-sm cursor-pointer text-left">Course Details</Link>
                <Link to={{ page: 'cma-experience-requirement' }} className="py-2 px-6 text-sm cursor-pointer text-left">Experience Requirement</Link>
                <Link to={{ page: 'cma-course-fee' }} className="py-2 px-6 text-sm cursor-pointer text-left">Course Fee</Link>
                <Link to={{ page: 'cma-faqs' }} className="py-2 px-6 text-sm cursor-pointer text-left">FAQs</Link>
            </MobileNavLinkAccordion>

            <MobileNavLinkAccordion title="Enroll with GFX" closeMobileMenu={() => setMobileMenuOpen(false)}>
                {cmaParts.map(p => <Link key={p.code} to={{ page: 'part-detail', params: { code: p.code } }} className="py-2 px-6 text-sm cursor-pointer text-left">{p.title}</Link>)}
                <Link to={{ page: 'cma-bundle-offer' }} className="py-2 px-6 text-sm font-semibold text-brand-accent dark:text-brand-accent-dark cursor-pointer text-left">Special Offer: Both Parts</Link>
            </MobileNavLinkAccordion>
            
            <Link to={{ page: 'testimonials' }} onClick={() => setMobileMenuOpen(false)} className="w-full text-left py-3 px-6 border-b border-slate-200 dark:border-zinc-800 font-semibold cursor-pointer">Testimonials</Link>

            <MobileNavLinkAccordion title="Resources" closeMobileMenu={() => setMobileMenuOpen(false)}>
                <Link to={{ page: 'demo-videos' }} className="py-2 px-6 text-sm cursor-pointer text-left">Demo Videos</Link>
                <Link to={{ page: 'articles' }} className="py-2 px-6 text-sm cursor-pointer text-left">Articles</Link>
                <Link to={{ page: 'official-resources' }} className="py-2 px-6 text-sm cursor-pointer text-left">Official Resources</Link>
            </MobileNavLinkAccordion>

            <Link to={{ page: 'about' }} onClick={() => setMobileMenuOpen(false)} className="w-full text-left py-3 px-6 border-b border-slate-200 dark:border-zinc-800 font-semibold cursor-pointer">About us</Link>

            <div className="flex items-center justify-center gap-4 py-4 w-full">
                <button
                    onClick={() => {
                        navigateTo({ page: 'cma-bundle-offer' });
                        setMobileMenuOpen(false);
                    }}
                    className="bg-brand-accent dark:bg-brand-accent-dark text-white font-bold py-2 px-6 rounded-full text-sm"
                >
                    Enroll Now
                </button>
                <button
                    onClick={handleContactClick}
                    className="bg-slate-200 dark:bg-zinc-700 text-slate-800 dark:text-slate-200 font-bold py-2 px-6 rounded-full text-sm"
                >
                    Contact Us
                </button>
              <Tooltip text={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}>
                <button
                  onClick={toggleTheme}
                  aria-label="Toggle theme"
                  className="p-2 rounded-full text-brand-accent dark:text-brand-accent-dark hover:bg-slate-800/10 dark:hover:bg-white/10"
                >
                  {theme === 'light' ? <MoonIcon className="w-5 h-5" /> : <SunIcon className="w-5 h-5" />}
                </button>
              </Tooltip>
            </div>
          </nav>
        </div>
      )}
      </header>
    </>
  );
};

export default Header;